import { h } from 'preact';
import clsx from 'clsx';
import { useEffect, useState } from 'preact/hooks';
import { FreeMode, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import ProductCard from 'utils/product-card';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import { edgesToNodes } from 'utils/storefront/format-resource';
import { isLauraCanada } from 'utils/helpers';

const CartUpSell = ({
  upSellData,
  cartEmpty = true,
  index,
  isOpen,
  lines,
  itemCount,
}) => {
  const storefront = Storefront.getInstance();
  const [products, setProducts] = useState([]);
  const {
    type,
    title,
    products: upSellItems,
    recommendationId,
  } = upSellData || {};

  const getProducts = async (data) => {
    const ids = data.map(
      (product) => `gid://shopify/Product/${product.product_id}`,
    );

    const response = await storefront.request({
      query: queries.getProductsByIds,
      variables: {
        ids,
      },
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error(
        'product-grid/product-grid.js: Storefront API request failed',
        response,
      );
      return [];
    }

    const existingProducts = response.data.nodes.filter(
      (product) => product != null && product.availableForSale,
    );

    if (!existingProducts.length) {
      return [];
    }

    return edgesToNodes(existingProducts, 'product');
  };

  const onRecommendationsLoaded = (data) => {
    if (data && data.length > 0) {
      // track event recommendation action=show
      // often, the recommended items are placed below the fold, for proper evalution track "show" event after customer scrolls and see recommended items
      const itemIds = data.map((product) => product.item_id);
      // eslint-disable-next-line no-undef
      exponea.track('recommendation', {
        action: 'show',
        item_ids: itemIds,
        recommendation_id: recommendationId,
        placement: 'cart',
      });
      getProducts(data).then((items) => setProducts(items));
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    // only load for appropriate conditions
    // if cart is empty and the upsell is meant for the populated cart, don't load
    if (itemCount && cartEmpty) return;
    // if cart is populated and the upsell is meant for the empty cart, don't load
    if (!itemCount && !cartEmpty) return;

    if (type === 'manual' && upSellItems?.length > 0) setProducts(upSellItems);
    else if (type === 'bloomreach' && recommendationId?.length > 0) {
      const options = {
        recommendationId,
        size: 10, // Specifies upper limit of the recommendations to return. Defaults to 10.
        callback: onRecommendationsLoaded,
        fillWithRandom: true, // If true, fills the recommendations with random items until size is reached. This is utilized when models cannot recommend enough items.
      };
      // call recommedations
      // eslint-disable-next-line no-undef
      exponea.getRecommendation(options);
    }
  }, [isOpen, lines]);

  useEffect(() => {
    if (products.length > 0) {
      document.dispatchEvent(new CustomEvent('cart-upsell:init'));
    }
  }, [products]);

  if (!products.length) return null;

  return (
    <div
      className="relative cart-upsell mb-16 md:mb-24"
      id={`cart-upsell--${index}`}
    >
      <div
        className={clsx('flex items-center relative', {
          'justify-end pt-16 md:pb-16': !title.length,
          'pl-24': cartEmpty,
          'pr-24': !title.length && cartEmpty,
          'mb-16 mt-24 ml:mt-32 ml:justify-center': title.length,
        })}
      >
        {title.length > 0 && <div className="display-5">{title}</div>}
        <div
          className={clsx('mini-cart__pagination hidden md:flex items-center', {
            'right-24': cartEmpty,
            'right-0': !cartEmpty,
            'absolute top-0': title.length > 0,
          })}
        >
          <button className="prev w-24 h-24 rounded-full text-grey-900 lc:border border-grey-900 flex justify-center items-center mx-4 lc:mx-8 pr-2">
            <svg
              className="w-12 ml:w-24 h-auto rotate-180"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 2.25L12.75 9L6 15.75"
                stroke="black"
                strokeWidth={isLauraCanada() ? 1 : 0.75}
              />
            </svg>
          </button>
          <button className="next w-24 h-24 rounded-full text-grey-900 lc:border border-grey-900 flex justify-center items-center pl-2">
            <svg
              className="w-12 ml:w-24 h-auto"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 2.25L12.75 9L6 15.75"
                stroke="black"
                strokeWidth={isLauraCanada() ? 1 : 0.75}
              />
            </svg>
          </button>
        </div>
      </div>

      <div
        className={clsx({
          'pl-24': cartEmpty,
          '-mr-24': !cartEmpty,
        })}
      >
        <Swiper
          modules={[Navigation, FreeMode]}
          navigation={{
            prevEl: `#cart-upsell--${index} .mini-cart__pagination .prev`,
            nextEl: `#cart-upsell--${index} .mini-cart__pagination .next`,
            disabledClass: 'text-disabled !border-grey-400',
          }}
          slidesPerView="auto"
          spaceBetween={8}
          freeMode={true}
          loop={true}
        >
          {products.map((product) => (
            <SwiperSlide key={product.id} className="!w-[135px]">
              <ProductCard
                key={product.id}
                product={product}
                placement="cart"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default CartUpSell;
